import { LocalizationService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { inject, Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { OAuthService } from 'angular-oauth2-oidc';
import { Subject } from 'rxjs';
import { environment } from 'src/environments/environment'; // Import environment

export interface INotificationPyload {
    concurrencyStamp: string | null;
    creationTime: string;
    creatorId: string | null;
    deleterId: string | null;
    deletionTime: string | null;
    iconClass: string;
    id: number;
    identityUserId: string;
    isDeleted: boolean;
    isRead: boolean;
    lastModificationTime: string | null;
    lastModifierId: string | null;
    title: string;
    count: number | null;
    referenceId: number | null;
  }

  
@Injectable({
  providedIn: 'root',
})
export class SignalRClientService {
  connection: HubConnection;

  private readonly oAuthService = inject(OAuthService);
  protected readonly toasterService = inject(ToasterService);
  private readonly localizationService = inject(LocalizationService);

  constructor() {
    const baseUrl = environment.apis.default.url; // Use the base URL from environment
    this.connection = new HubConnectionBuilder()
      .withUrl(`${baseUrl}/ReturnLySignalRHub`, {
        accessTokenFactory: () => this.oAuthService.getAccessToken(),
      })
      .build();
    this.connection.on('NotificationMessage', this.handleMessage);

    this.connection.start().catch(err => console.error(err.toString()));
  }

  /*
   * A Subject, not a single callback field. This service is `providedIn: 'root'`, so there is
   * exactly one instance, but more than one Notification component is alive at a time (the mobile
   * one in app.component.html and the desktop one injected via navItems.addItems). The previous
   * `useOnMessage` *assigned* the callback, so whichever component constructed second silently
   * overwrote the first and only one bell ever refreshed. A Subject fans the message out to every
   * subscriber, and subscribers can unsubscribe when their component is destroyed.
   */
  private readonly message = new Subject<INotificationPyload>();
  readonly message$ = this.message.asObservable();

  handleMessage = (response: INotificationPyload) => {
    /*
     * Localized here rather than handed to the toaster as a key, because the toaster takes a key
     * with no interpolation parameters and the key for a run of several matches carries the count
     * as {0}. A resolved string has no `::` prefix, so it passes through the toaster untouched.
     */
    const title = this.localizationService.instant(
      { key: `::${response.title}`, defaultValue: response.title },
      response.count?.toString() ?? '',
    );

    this.toasterService.success('', title, {
        iconClass: response.iconClass,
        closable:false
    });
    this.message.next(response);
  };
}
